import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Organization } from 'fhir/r4';
import { authBackendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';

@Component({
  selector: 'app-patient-hospital-selection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule
  ],
  templateUrl: './patient-hospital-selection.component.html',
  styleUrls: ['./patient-hospital-selection.component.scss']
})
export class PatientHospitalSelectionComponent implements OnInit {
  private http = inject(HttpClient);
  private router = inject(Router);
  private backendUrl = inject(authBackendEndPointToken);
  private errorService = inject(ErrorService);

  hospitals: Organization[] = [];
  filteredHospitals: Organization[] = [];
  paginatedHospitals: Organization[] = [];
  loading = true;
  selectedHospitalId: string | null = null;
  currentYear = new Date().getFullYear();
  searchQuery = '';

  // Pagination
  pageSize = 6;
  pageIndex = 0;
  totalItems = 0;

  ngOnInit(): void {
    // Verify this is a patient registration flow
    const registrationFlow = sessionStorage.getItem('registrationFlow');
    if (registrationFlow !== 'patient-self-registration') {
      this.router.navigate(['/']);
      return;
    }

    this.loadHospitals();
  }

  /**
   * Fetch all active hospitals/organizations from the backend
   */
  loadHospitals(): void {
    this.loading = true;
    
    this.http.get<any>(`${this.backendUrl}/api/auth/Organization?active=true`)
      .subscribe({
        next: (response) => {
          console.log('📦 Raw response from backend:', response);
          
          if (response.success && response.data) {
            console.log(`✅ Fetched ${response.data.length} organizations from auth backend for patient selection`);
            
            this.hospitals = response.data.map((org: any) => ({
              id: org.id,
              name: org.name,
              type: org.type ? [{ text: org.type }] : [],
              telecom: [
                ...(org.phone ? [{ system: 'phone', value: org.phone }] : []),
                ...(org.email ? [{ system: 'email', value: org.email }] : [])
              ],
              address: org.address ? [{ 
                city: org.address,
                line: [],
                state: '',
                country: ''
              }] : [],
              alias:  org.description?.value ? [org.description.value] : [],
              active: org.active
            }));
            
            console.log(`✅ Loaded ${this.hospitals.length} active hospitals for patient selection`);
            this.applyFilter();
            this.loading = false;
          } else {
            throw new Error('Invalid response format');
          }
        },
        error: (err) => {
          console.error('❌ Failed to load hospitals:', err);
          this.errorService.openandCloseError('Failed to load hospitals. Please try again.');
          this.loading = false;
        }
      });
  }

  /**
   * Apply search filter
   */
  applyFilter(): void {
    const query = this.searchQuery.toLowerCase().trim();
    
    if (!query) {
      this.filteredHospitals = [...this.hospitals];
    } else {
      this.filteredHospitals = this.hospitals.filter(hospital => {
        const name = (hospital.name || '').toLowerCase();
        const type = this.getHospitalType(hospital).toLowerCase();
        const address = this.getHospitalAddress(hospital).toLowerCase();
        const phone = this.getHospitalPhone(hospital)?.toLowerCase() || '';
        const email = this.getHospitalEmail(hospital)?.toLowerCase() || '';
        
        return name.includes(query) || 
               type.includes(query) || 
               address.includes(query) ||
               phone.includes(query) ||
               email.includes(query);
      });
    }
    
    this.totalItems = this.filteredHospitals.length;
    this.pageIndex = 0; // Reset to first page when filtering
    this.updatePaginatedHospitals();
  }

  /**
   * Update paginated hospitals based on current page
   */
  updatePaginatedHospitals(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedHospitals = this.filteredHospitals.slice(startIndex, endIndex);
  }

  /**
   * Handle page change event
   */
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedHospitals();
    
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Clear search filter
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  /**
   * Handle hospital selection and navigate to patient registration
   */
  onSelectHospital(hospital: Organization): void {
    if (!hospital.id) {
      this.errorService.openandCloseError('Invalid hospital selection');
      return;
    }

    console.log('✅ Patient selected hospital:', hospital);
    this.selectedHospitalId = hospital.id;

    sessionStorage.setItem('selectedHospitalId', hospital.id);
    sessionStorage.setItem('selectedHospitalName', hospital.name || 'Hospital');
    sessionStorage.setItem('registrationFlow', 'patient-self-registration');

    this.router.navigate(['/register']);
  }

  /**
   * Cancel and return to home page
   */
  onCancel(): void {
    sessionStorage.removeItem('registrationFlow');
    sessionStorage.removeItem('selectedHospitalId');
    sessionStorage.removeItem('selectedHospitalName');
    this.router.navigate(['/']);
  }

  /**
   * Get formatted address from Organization
   */
  getHospitalAddress(hospital: Organization): string {
    if (!hospital.address || hospital.address.length === 0) {
      return 'Address not available';
    }

    const address = hospital.address[0];
    const parts: string[] = [];

    if (address.line && address.line.length > 0) {
      parts.push(address.line.join(', '));
    }
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);

    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  }

  /**
   * Get hospital type/category for display
   */
  getHospitalType(hospital: Organization): string {
    if (!hospital.type || hospital.type.length === 0) {
      return 'Healthcare Facility';
    }

    const type = hospital.type[0];
    return type.text || 
           type.coding?.[0]?.display || 
           type.coding?.[0]?.code || 
           'Healthcare Facility';
  }

  /**
   * Get hospital contact phone number
   */
  getHospitalPhone(hospital: Organization): string | null {
    if (!hospital.telecom || hospital.telecom.length === 0) {
      return null;
    }

    const phoneContact = hospital.telecom.find(t => t.system === 'phone');
    return phoneContact?.value || null;
  }

  /**
   * Get hospital contact email
   */
  getHospitalEmail(hospital: Organization): string | null {
    if (!hospital.telecom || hospital.telecom.length === 0) {
      return null;
    }

    const emailContact = hospital.telecom.find(t => t.system === 'email');
    return emailContact?.value || null;
  }

  /**
   * Check if hospital is currently selected
   */
  isSelected(hospital: Organization): boolean {
    return this.selectedHospitalId === hospital.id;
  }
}